import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import { formatFileSize, formatDuration } from '../services/api.service';
import template from './media-upload-progress.html';

export interface UploadProgressItem {
  id: string;
  fileName: string;
  size: number;
  uploadedBytes: number;
  status: 'queued' | 'uploading' | 'done' | 'error' | 'duplicate';
  message?: string;
}

// A single modal summarizing an entire drag-and-drop upload batch: overall count/size
// progress plus an estimated time remaining, instead of one toast per file.
@customElement({ name: 'media-upload-progress', template })
export class MediaUploadProgress {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public visible = false;
  @bindable public items: UploadProgressItem[] = [];
  @bindable public startedAt: number | null = null;
  @bindable public closed!: () => void;

  public get totalFiles(): number {
    return this.items.length;
  }

  public get completedCount(): number {
    return this.items.filter(item => item.status === 'done' || item.status === 'error' || item.status === 'duplicate').length;
  }

  public get errorCount(): number {
    return this.items.filter(item => item.status === 'error').length;
  }

  public get duplicateCount(): number {
    return this.items.filter(item => item.status === 'duplicate').length;
  }

  public get totalBytes(): number {
    return this.items.reduce((sum, item) => sum + (item.size || 0), 0);
  }

  public get uploadedBytes(): number {
    return this.items.reduce((sum, item) => sum + ((item.status === 'done' || item.status === 'duplicate') ? item.size : item.uploadedBytes), 0);
  }

  public get isSizeBased(): boolean {
    return this.totalBytes > 0;
  }

  public get allDone(): boolean {
    return this.totalFiles > 0 && this.completedCount === this.totalFiles;
  }

  // Only files still in play are worth showing: the one uploading now, the ones still
  // queued behind it, and any failures needing attention. Finished files are dropped from
  // the list so it visibly shrinks as the batch progresses, with the active file kept on top.
  public get visibleItems(): UploadProgressItem[] {
    const rank: Record<UploadProgressItem['status'], number> = { uploading: 0, queued: 1, error: 2, done: 3, duplicate: 3 };
    return this.items
      .filter(item => item.status !== 'done' && item.status !== 'duplicate')
      .sort((a, b) => rank[a.status] - rank[b.status]);
  }

  public get overallPercent(): number {
    if (this.isSizeBased) {
      return Math.min(100, Math.round((this.uploadedBytes / this.totalBytes) * 100));
    }
    return this.totalFiles > 0 ? Math.round((this.completedCount / this.totalFiles) * 100) : 0;
  }

  public get filesLabel(): string {
    return this.i18n.tr('gallery.upload.filesProgress', { done: this.completedCount, total: this.totalFiles });
  }

  public get duplicatesLabel(): string {
    if (this.duplicateCount === 0) return '';
    return this.i18n.tr('gallery.upload.duplicatesSkipped', { count: this.duplicateCount });
  }

  public get sizeLabel(): string {
    if (!this.isSizeBased) return '';
    return this.i18n.tr('gallery.upload.sizeProgress', {
      done: formatFileSize(this.uploadedBytes),
      total: formatFileSize(this.totalBytes),
    });
  }

  public get remainingLabel(): string {
    if (!this.isSizeBased || this.allDone) return '';
    const remaining = this.totalBytes - this.uploadedBytes;
    if (remaining <= 0) return '';
    return this.i18n.tr('gallery.upload.remaining', { size: formatFileSize(remaining) });
  }

  public get etaLabel(): string {
    if (this.allDone || !this.isSizeBased || !this.startedAt) return '';
    const elapsedMs = Date.now() - this.startedAt;
    const remainingBytes = this.totalBytes - this.uploadedBytes;
    if (elapsedMs < 1000 || this.uploadedBytes <= 0 || remainingBytes <= 0) return '';
    const bytesPerMs = this.uploadedBytes / elapsedMs;
    const remainingMs = remainingBytes / bytesPerMs;
    return this.i18n.tr('gallery.upload.timeRemaining', { time: formatDuration(remainingMs) });
  }

  public itemSizeLabel(item: UploadProgressItem): string {
    return formatFileSize(item.size);
  }

  public itemPercent(item: UploadProgressItem): number {
    if (!item.size) return item.status === 'done' ? 100 : 0;
    return Math.min(100, Math.round((item.uploadedBytes / item.size) * 100));
  }

  public statusMessage(item: UploadProgressItem): string {
    return item.message || this.i18n.tr(`gallery.upload.${item.status}`);
  }

  public close(): void {
    if (this.allDone && this.closed) this.closed();
  }
}
